/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";

import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FiRefreshCw, FiEye } from "react-icons/fi";
import StatCard from "../components/StatCard";
import Badges from "../components/Badges";
import AccessControl from "../components/AccessControl";
import { getAllRideByAdmin } from "../api/rideApi";

// Same status map used in Bookings.jsx, kept in one place so the badge text
// on Dashboard and Bookings always stays in sync.
const STATUS_MAP = {
  trip_completed: "Completed",
  trip_started: "Ongoing",
  requested: "Pending",
  canceled: "Cancelled",
};

// Fixed avatar palette so each driver gets a consistent-looking colour
// without needing a colour field from the backend.
const AVATAR_COLORS = [
  { background: "#E1F5EE", color: "#0F6E56" },
  { background: "#E6F1FB", color: "#185FA5" },
  { background: "#FAEEDA", color: "#854F0B" },
  { background: "#FCEBEB", color: "#A32D2D" },
  { background: "#F1EBFB", color: "#5B3FA0" },
];

const ZONE_COLORS = ["#1D9E75", "#378ADD", "#EF9F27", "#D4537E", "#7F77DD"];

const DATE_FILTERS = ["All", "Today", "Yesterday", "This Month", "Custom"];
const DATE_FILTER_PARAM = {
  All: "all",
  Today: "today",
  Yesterday: "yesterday",
  "This Month": "month",
  Custom: "custom",
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "–";

const isSameDay = (dateStr, ref) =>
  new Date(dateStr).toDateString() === ref.toDateString();

export default function Dashboard() {
  const navigate = useNavigate();

  const [rides, setRides] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ── Recent booking "View" modal — pickup/drop shown on a map ────────
  const [viewBooking, setViewBooking] = useState(null);
  const openViewModal = (b) => setViewBooking(b);
  const closeViewModal = () => setViewBooking(null);

  // ── Date filter state (mirrors Bookings.jsx) ────────────────────────
  const [dateFilter, setDateFilter] = useState("All");
  const [customFrom, setCustomFrom] = useState(""); // yyyy-mm-dd input value
  const [customTo, setCustomTo] = useState("");
  // Only the *applied* custom range triggers a fetch — typing shouldn't
  // refetch on every keystroke, only "Apply" should.
  const [appliedCustomRange, setAppliedCustomRange] = useState({
    from: "",
    to: "",
  });

  // 🔁 Fetch rides from backend — same call Bookings.jsx uses, just with a
  // high limit so the dashboard has enough rows to summarize from.
  const fetchRides = async () => {
    setLoading(true);
    setError("");
    const payload = {
      n_limit: 500,
      n_page: 1,
      c_search_term: "",
      date_filter: DATE_FILTER_PARAM[dateFilter] || "all",
      ...(dateFilter === "Custom"
        ? { from_date: appliedCustomRange.from, to_date: appliedCustomRange.to }
        : {}),
    };
    const res = await getAllRideByAdmin(payload);
    if (res.success) {
      console.log(res.rides,"<<< res.rides")
      setRides(res.rides || []);
      setTotal(res.total || 0);
    } else {
      setError(res.error || "Failed to load dashboard data.");
    }
    setLoading(false);
  };

  useEffect(() => {
    // Custom range needs both dates picked and "Apply" pressed before we
    // fetch — otherwise every keystroke in the date inputs would refetch.
    if (
      dateFilter === "Custom" &&
      (!appliedCustomRange.from || !appliedCustomRange.to)
    ) {
      return;
    }
    fetchRides();
  }, [dateFilter, appliedCustomRange]);

  const selectDateFilter = (f) => setDateFilter(f);

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    setAppliedCustomRange({ from: customFrom, to: customTo });
  };

  // rides is already scoped to the selected date filter by the backend,
  // so the stat cards below summarize whatever range is currently active.
  const rangeLabel = dateFilter === "Custom" ? "Custom range" : dateFilter;

  const cancelledRides = rides.filter((r) => r.status === "canceled");
  const revenue = rides
    .filter((r) => r.paymentStatus === "paid")
    .reduce((sum, r) => sum + (r.fare || 0), 0);
  const activeDriverIds = new Set(
    rides.filter((r) => r.driverId).map((r) => r.driverId),
  );

  // ── Weekly bookings chart (last 7 days) ─────────────────────────────
  // const weeklyData = Array.from({ length: 7 }, (_, i) => {
  //   const d = new Date();
  //   d.setDate(d.getDate() - (6 - i));
  //   return {
  //     day: d.toLocaleDateString("en-US", { weekday: "short" }),
  //     bookings: rides.filter((r) => isSameDay(r.createdAt, d)).length,
  //   };
  // });

  const isCompleted = (r) => r.status === "trip_completed";
const isCanceled = (r) => r.status === "canceled";

// ── Weekly bookings chart (last 7 days) — status wise ───────────────
const weeklyData = Array.from({ length: 7 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - (6 - i));

  const dayRides = rides.filter((r) => isSameDay(r.createdAt, d));
  const completed = dayRides.filter(isCompleted).length;
  const canceled = dayRides.filter(isCanceled).length;

  return {
    day: d.toLocaleDateString("en-US", { weekday: "short" }),
    completed,
    canceled,
    // requested / confirmed / trip_started etc. — anything still in progress
    ongoing: dayRides.length - completed - canceled,
  };
});

// ── Today summary ───────────────────────────────────────────────────
const todayRides = rides.filter((r) => isSameDay(r.createdAt, new Date()));
const todayCompleted = todayRides.filter(isCompleted).length;
const todayCanceled = todayRides.filter(isCanceled).length;
const todayOngoing = todayRides.length - todayCompleted - todayCanceled;

  // ── Top drivers today (by driver, aggregated from fetched rides) ───
  const driverMap = {};
  rides.forEach((r) => {
    if (!r.driverId || !r.driver) return;
    if (!driverMap[r.driverId]) {
      driverMap[r.driverId] = {
        id: r.driverId,
        name: r.driver.name,
        rating: r.driver.avgRating ?? r.driver.rating ?? "–",
        zoneName: r.driver.zoneName || "–",
        vehicleNumber: r.driver.vehicleNumber || "–",
        rides: 0,
        earnings: 0,
      };
    }
    driverMap[r.driverId].rides += 1;
    if (r.paymentStatus === "paid") {
      driverMap[r.driverId].earnings += r.fare || 0;
    }
  });
  const topDrivers = Object.values(driverMap)
    .sort((a, b) => b.earnings - a.earnings)
    .slice(0, 4)
    .map((d, i) => ({ ...d, ...AVATAR_COLORS[i % AVATAR_COLORS.length] }));

   

  // ── Zone performance ─────────────────────────────────────────────────
  const zoneMap = {};
  rides.forEach((r) => {
    const zone = r.driver?.zoneName;
    if (!zone) return;
    zoneMap[zone] = (zoneMap[zone] || 0) + 1;
  });
  const zoneTotal = Object.values(zoneMap).reduce((a, b) => a + b, 0);
  const zoneData = Object.entries(zoneMap)
    .map(([zone, count]) => ({
      zone,
      pct: zoneTotal ? Math.round((count / zoneTotal) * 100) : 0,
    }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 5);

  // ── Recent bookings (latest 6) ───────────────────────────────────────
  const recentBookings = [...rides]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 6);

  return (
    <div>
      <AccessControl
        allowedRoles={[
          "SUPERADMIN",
          "ADMIN",
          "NSM",
          "DSM",
          "RSM",
          "ASM",
          "TSO",
        ]}
        redirectTo="/404"
      />

      {/* Filters + Refresh */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div className="filter-tabs" style={{ margin: 0 }}>
            {DATE_FILTERS.map((f) => (
              <button
                key={f}
                className={`filter-tab ${dateFilter === f ? "active" : ""}`}
                onClick={() => selectDateFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>

          {dateFilter === "Custom" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "6px 8px",
                  fontSize: 12,
                }}
              />
              <span style={{ color: "#888", fontSize: 12 }}>to</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "6px 8px",
                  fontSize: 12,
                }}
              />
              <button
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
                className="btn-outline-green btn-sm"
              >
                Apply
              </button>
            </div>
          )}
        </div>

        <button onClick={fetchRides} className="btn-outline-green btn-sm">
          <FiRefreshCw
            style={{
              marginRight: 6,
              animation: loading ? "spin 1s linear infinite" : "none",
            }}
          />
          Refresh
        </button>
      </div>

      {error && (
        <div
          className="card section"
          style={{ color: "#A32D2D", fontSize: 13 }}
        >
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="stat-grid section">
        <StatCard
          label="Total Bookings"
          value={total.toLocaleString()}
          change={rangeLabel}
          changeType="up"
          iconBg="#E1F5EE"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#0F6E56">
              <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
            </svg>
          }
        />
        <StatCard
          label="Active Drivers"
          value={activeDriverIds.size}
          change={`${topDrivers.length} in top list`}
          changeType="up"
          iconBg="#E6F1FB"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#185FA5">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z" />
              <circle cx="7.5" cy="14.5" r="1.5" />
              <circle cx="16.5" cy="14.5" r="1.5" />
            </svg>
          }
        />
        <StatCard
          label="Total Revenue"
          value={`₹${revenue.toLocaleString()}`}
          change={rangeLabel}
          changeType="up"
          iconBg="#FAEEDA"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#854F0B">
              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
            </svg>
          }
        />
        <StatCard
          label="Cancelled Rides"
          value={cancelledRides.length}
          change={rangeLabel}
          changeType="down"
          iconBg="#FCEBEB"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#A32D2D">
              <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
            </svg>
          }
        />
      </div>

      {/* Chart + Top Drivers */}
      <div className="grid-2 section">
        {/* <div className="card">
          <div className="card-header">
            <span className="card-title">Weekly Bookings</span>
            <span className="badge badge-green" style={{ fontSize: 11 }}>
              Last 7 Days
            </span>
          </div>
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={weeklyData} barSize={28}>
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11, fill: "#888" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#888" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #e8e8e4",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "#f5f5f0" }}
                />
                <Bar dataKey="bookings" fill="#1D9E75" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div> */}

        <div className="card">
  <div className="card-header">
    <span className="card-title">Weekly Bookings</span>
    <span className="badge badge-green" style={{ fontSize: 11 }}>
      Last 7 Days
    </span>
  </div>

  {/* 📊 Today summary */}
  <div style={{ display: "flex", gap: 8, padding: "0 16px 12px", flexWrap: "wrap" }}>
    {[
      { label: "Today", value: todayRides.length, bg: "#f0f0ec", color: "#1a1a1a" },
      { label: "Completed", value: todayCompleted, bg: "#E1F5EE", color: "#0F6E56" },
      { label: "Cancelled", value: todayCanceled, bg: "#FCEBEB", color: "#A32D2D" },
      { label: "Ongoing", value: todayOngoing, bg: "#FAEEDA", color: "#854F0B" },
    ].map((s) => (
      <div
        key={s.label}
        style={{
          background: s.bg,
          color: s.color,
          borderRadius: 8,
          padding: "6px 12px",
          minWidth: 72,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700 }}>{s.value}</div>
        <div style={{ fontSize: 11 }}>{s.label}</div>
      </div>
    ))}
  </div>

  <div className="chart-wrap">
    <ResponsiveContainer width="100%" height={190}>
      <BarChart data={weeklyData} barSize={28}>
        <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: "#fff",
            border: "1px solid #e8e8e4",
            borderRadius: 8,
            fontSize: 12,
          }}
          cursor={{ fill: "#f5f5f0" }}
        />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="completed" name="Completed" stackId="a" fill="#1D9E75" />
        <Bar dataKey="ongoing" name="Ongoing" stackId="a" fill="#EF9F27" />
        <Bar dataKey="canceled" name="Cancelled" stackId="a" fill="#E24B4A" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Drivers</span>
            <span className="card-action" onClick={() => navigate("/drivers")}>
              See all
            </span>
          </div>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888" }}>
              Loading...
            </div>
          ) : topDrivers.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888" }}>
              No driver activity yet
            </div>
          ) : (
            topDrivers.map((d) => (
              <div
                key={d.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  paddingBottom: 12,
                  marginBottom: 12,
                  borderBottom: "1px solid #f0f0ec",
                }}
              >
                <div
                  className="avatar"
                  style={{ background: d.background, color: d.color }}
                >
                  {getInitials(d.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}
                  >
                    {d.name}
                  </div>
                 
                  <div style={{ fontSize: 11, color: "#888" }}>
                    {d.rides} rides · {d.rating} ★
                  </div>
                  <div style={{ fontSize: 11, color: "#888" }}>
                     {d.vehicleNumber}
                  </div>
                </div>
                 <div style={{ fontSize: 11, color: "#888" }}>
                    {d.zoneName}
                  </div>
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#0F6E56" }}
                >
                  ₹{d.earnings.toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      

      {/* Recent Bookings */}
      <div className="grid-2 section">
        <div className="card col-span-2">
          <div className="card-header">
            <span className="card-title">Recent Bookings</span>
            <span className="card-action" onClick={() => navigate("/bookings")}>
              View all
            </span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Driver</th>
                  <th>Route</th>
                  <th>Fare</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", padding: 30 }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : recentBookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: "center",
                        padding: 30,
                        color: "#888",
                      }}
                    >
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  recentBookings.map((b) => (
                    <tr key={b._id}>
                      <td style={{ fontWeight: 600, color: "#0F6E56" }}>
                        {b.rideId}
                      </td>
                      <td>{b.passenger?.name || b.passengerName || "–"}</td>
                      <td style={{ color: "#888" }}>{b.driver?.name || "–"}</td>
                      <td>
                        {b.pickupLocation?.address?.split(",")[0]} →{" "}
                        {b.dropLocation?.address?.split(",")[0]}
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        ₹{b.fare?.toFixed(2) ?? "–"}
                      </td>
                      <td>
                        <Badges status={STATUS_MAP[b.status] || b.status} />
                      </td>
                      <td>
                        <button
                          onClick={() => openViewModal(b)}
                          title="View pickup & drop on map"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "none",
                            border: "1px solid #0F6E56",
                            color: "#0F6E56",
                            borderRadius: 6,
                            padding: "4px 8px",
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <FiEye /> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Zone Performance */}
      <div className="card section">
        <div className="card-header">
          <span className="card-title">Zone Performance</span>
          <span style={{ fontSize: 12, color: "#888" }}>
            Booking volume by area
          </span>
        </div>
        {zoneData.length === 0 ? (
          <div style={{ padding: 12, color: "#888", fontSize: 13 }}>
            No zone data yet
          </div>
        ) : (
          zoneData.map((z, i) => (
            <div key={z.zone} className="progress-bar-wrap">
              <div className="progress-bar-label">
                <span>{z.zone}</span>
                <span style={{ fontWeight: 600 }}>{z.pct}%</span>
              </div>
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{
                    width: `${z.pct}%`,
                    background: ZONE_COLORS[i % ZONE_COLORS.length],
                  }}
                ></div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── View Booking Modal — pickup/drop on a map ────────────────── */}
      {viewBooking && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={closeViewModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 24,
              width: "100%",
              maxWidth: 560,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 4px" }}>{viewBooking.rideId}</h3>
            <p style={{ margin: "0 0 16px", color: "#888", fontSize: 13 }}>
              {viewBooking.passenger?.name ||
                viewBooking.passengerName ||
                "Passenger"}
              {viewBooking.driver?.name ? ` · ${viewBooking.driver.name}` : ""}
              {" · "}
              <Badges
                status={STATUS_MAP[viewBooking.status] || viewBooking.status}
              />
            </p>

            {/* Map showing pickup → drop */}
            {viewBooking.pickupLocation?.latitude &&
            viewBooking.dropLocation?.latitude ? (
              <div
                style={{
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #eee",
                  marginBottom: 16,
                }}
              >
                <iframe
                  title="Ride route"
                  width="100%"
                  height="280"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?saddr=${viewBooking.pickupLocation.latitude},${viewBooking.pickupLocation.longitude}&daddr=${viewBooking.dropLocation.latitude},${viewBooking.dropLocation.longitude}&output=embed`}
                />
              </div>
            ) : (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#888",
                  fontSize: 13,
                  border: "1px dashed #ddd",
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                Location coordinates aren't available for this ride.
              </div>
            )}

            {/* Pickup / Drop address details */}
            <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#1D9E75",
                  marginTop: 5,
                  flexShrink: 0,
                }}
              />
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>PICKUP</div>
                <div style={{ fontSize: 13, color: "#1a1a1a" }}>
                  {viewBooking.pickupLocation?.address || "–"}
                </div>
              </div>
            </div>
            <div
              style={{
                borderLeft: "1px dashed #ccc",
                marginLeft: 3,
                height: 14,
              }}
            />
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#A32D2D",
                  marginTop: 5,
                  flexShrink: 0,
                }}
              />
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>DROP</div>
                <div style={{ fontSize: 13, color: "#1a1a1a" }}>
                  {viewBooking.dropLocation?.address || "–"}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={closeViewModal}
                style={{
                  background: "none",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "8px 16px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
